/**
 * Worked time as the BUSINESS sees it: which day a shift belongs to, what a pay
 * period adds up to, what the payroll file contains, and what the history says.
 *
 * HQ-PAYROLL-001. Workflow map, Worked time to approved payroll, step 2: "use the
 * employee's local business date so entries near UTC midnight cannot be stored or
 * summarized under the wrong day". The payroll page grouped shifts by the VIEWER's
 * browser clock (todayISO), so the same shift belonged to a different day depending
 * on where an administrator was sitting, and the day boundaries of the date filter
 * moved with them. Everything here uses one timezone, written once: where the
 * business is. It matches hq_business_tz() in 2027-02-07_time_to_payroll.sql, and a
 * test fails if the two differ.
 *
 * Pure. No I/O.
 */

export const BUSINESS_TZ = "America/Chicago";

const dayFmt = new Intl.DateTimeFormat("en-CA", { timeZone: BUSINESS_TZ, year: "numeric", month: "2-digit", day: "2-digit" });
const timeFmt = new Intl.DateTimeFormat("en-US", { timeZone: BUSINESS_TZ, hour: "numeric", minute: "2-digit" });

/** YYYY-MM-DD of this moment in the business's timezone. */
export function businessDay(iso: string | Date): string {
  return dayFmt.format(typeof iso === "string" ? new Date(iso) : iso);
}

/** Today, where the business is. */
export function businessToday(now: Date = new Date()): string {
  return businessDay(now);
}

/** "9:05 AM" in the business's timezone. */
export function businessTime(iso: string | null | undefined): string {
  return iso ? timeFmt.format(new Date(iso)) : "";
}

/** What the zone's wall clock is ahead of UTC, in ms, at this instant. */
function zoneOffsetMs(d: Date): number {
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: BUSINESS_TZ, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric",
    hour: "numeric", minute: "numeric", second: "numeric",
  }).formatToParts(d);
  const get = (t: string) => Number(p.find(x => x.type === t)!.value);
  const wall = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return wall - Math.floor(d.getTime() / 1000) * 1000;
}

/** The instant a business day begins (local midnight), as UTC. Correct across daylight saving. */
export function businessDayStart(day: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  const guess = Date.UTC(y, m - 1, d);
  const off = zoneOffsetMs(new Date(guess));
  let t = guess - off;
  const off2 = zoneOffsetMs(new Date(t));
  if (off2 !== off) t = guess - off2;
  return new Date(t);
}

/** [start, end) in UTC covering the business days from..to inclusive. */
export function businessRange(from: string, to: string): { startIso: string; endIso: string } {
  const [y, m, d] = to.split("-").map(Number);
  const nextDay = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  return { startIso: businessDayStart(from).toISOString(), endIso: businessDayStart(nextDay).toISOString() };
}

// ── Summaries ────────────────────────────────────────────────────────────────

export interface TimeLogLite {
  id: string; employee_id: string;
  clock_in_time: string; clock_out_time: string | null;
  hours_worked: number | null;
  manual_entry?: boolean | null; verification_status?: string | null; notes?: string | null;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Hours per business day, in date order. A shift belongs to the day it STARTED. */
export function dailyTotals(logs: TimeLogLite[]): { day: string; hours: number; logs: number; open: number }[] {
  const m = new Map<string, { hours: number; logs: number; open: number }>();
  for (const l of logs) {
    const day = businessDay(l.clock_in_time);
    const cur = m.get(day) ?? { hours: 0, logs: 0, open: 0 };
    cur.hours += l.hours_worked ?? 0; cur.logs += 1; if (!l.clock_out_time) cur.open += 1;
    m.set(day, cur);
  }
  return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([day, v]) => ({ day, hours: r2(v.hours), logs: v.logs, open: v.open }));
}

/** Total hours per employee. */
export function hoursByEmployee(logs: TimeLogLite[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const l of logs) out[l.employee_id] = r2((out[l.employee_id] ?? 0) + (l.hours_worked ?? 0));
  return out;
}

export type AckState = "none" | "current" | "changed";

/** Has this person confirmed their hours, and are those still the hours? */
export function ackState(ack: { hours: number } | null | undefined, currentHours: number): AckState {
  if (!ack) return "none";
  return Math.abs(r2(ack.hours) - r2(currentHours)) < 0.005 ? "current" : "changed";
}

// ── The payroll file ─────────────────────────────────────────────────────────

export interface PayrollEmployee {
  id: string; first_name: string | null; last_name: string | null; email: string; employee_number?: string | null;
}

export const PAYROLL_HEADERS = [
  "Employee ID", "Employee", "Email", "Business date", "Clock in", "Clock out", "Hours",
  "Manual entry", "Adjusted", "Verification", "Notes",
];

/**
 * One row per time log. The employee is identified by a number that does not change
 * when their name or email does (employee_number, else the HQ id) — the email alone
 * is what the rebrand rewrote for everyone at once. `adjusted` marks a log somebody
 * else changed after it was recorded, so payroll can see which hours were corrected.
 */
export function payrollRows(
  logs: TimeLogLite[], employees: Record<string, PayrollEmployee>, adjustedLogIds: ReadonlySet<string>,
): (string | number)[][] {
  return [...logs]
    .sort((a, b) => a.clock_in_time.localeCompare(b.clock_in_time))
    .map(l => {
      const e = employees[l.employee_id];
      return [
        e?.employee_number || l.employee_id,
        [e?.first_name, e?.last_name].filter(Boolean).join(" ") || (e?.email ?? ""),
        e?.email ?? "",
        businessDay(l.clock_in_time),
        businessTime(l.clock_in_time),
        l.clock_out_time ? businessTime(l.clock_out_time) : "",
        l.hours_worked ?? "",
        l.manual_entry ? "yes" : "no",
        adjustedLogIds.has(l.id) ? "yes" : "no",
        l.verification_status ?? "",
        l.notes ?? "",
      ];
    });
}

// ── The history, in words ────────────────────────────────────────────────────

export interface AuditRowLite {
  id: string; log_id: string; employee_id: string | null; action: "insert" | "update" | "delete";
  changed_by: string | null; reason: string | null; created_at: string;
  before: Record<string, unknown> | null; after: Record<string, unknown> | null;
}

const when = (v: unknown) => (typeof v === "string" ? `${businessDay(v)} ${businessTime(v)}` : "—");
const hrs = (v: unknown) => (v == null ? "—" : `${Number(v).toFixed(2)} hr`);

/** What changed, one short line per field. */
export function describeChange(a: AuditRowLite): string[] {
  const b = a.before ?? {}, n = a.after ?? {};
  if (a.action === "insert") {
    return [`Added ${when(n.clock_in_time)} to ${n.clock_out_time ? when(n.clock_out_time) : "no clock-out"} (${hrs(n.hours_worked)})`];
  }
  if (a.action === "delete") {
    return [`Deleted ${when(b.clock_in_time)} to ${b.clock_out_time ? when(b.clock_out_time) : "no clock-out"} (${hrs(b.hours_worked)})`];
  }
  const out: string[] = [];
  if (b.clock_in_time !== n.clock_in_time) out.push(`Clock in ${when(b.clock_in_time)} → ${when(n.clock_in_time)}`);
  if (b.clock_out_time !== n.clock_out_time) out.push(`Clock out ${b.clock_out_time ? when(b.clock_out_time) : "none"} → ${n.clock_out_time ? when(n.clock_out_time) : "none"}`);
  if (Number(b.hours_worked ?? NaN) !== Number(n.hours_worked ?? NaN)) out.push(`Hours ${hrs(b.hours_worked)} → ${hrs(n.hours_worked)}`);
  if ((b.notes ?? "") !== (n.notes ?? "")) out.push(`Note “${b.notes ?? ""}” → “${n.notes ?? ""}”`);
  if (b.verification_status !== n.verification_status) out.push(`Status ${b.verification_status ?? "—"} → ${n.verification_status ?? "—"}`);
  return out.length ? out : ["No visible change"];
}

export const PERIOD_STATUS_LABEL: Record<string, string> = {
  open: "Open", approved: "Approved and locked", exported: "Exported", accepted: "Accepted by payroll",
};

export const EXCEPTION_LABEL: Record<string, string> = {
  open_log: "Still clocked in", short_shift: "Very short shift", over_daily: "Over the daily limit",
};

/** A period's dates, as the person reads them. */
export function periodLabel(startDate: string, endDate: string): string {
  const f = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${f(startDate)} – ${f(endDate)}`;
}

// ── Date-time inputs, on the business's clock ────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, "0");

/** "2026-10-05T21:30" — what the business's wall clock read at this instant, for a datetime-local box. */
export function isoToBusinessInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const wall = new Date(d.getTime() + zoneOffsetMs(d));
  return `${wall.getUTCFullYear()}-${pad2(wall.getUTCMonth() + 1)}-${pad2(wall.getUTCDate())}T${pad2(wall.getUTCHours())}:${pad2(wall.getUTCMinutes())}`;
}

/** The instant a datetime-local value means, read as the business's clock — not the browser's. */
export function businessInputToIso(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local ?? "");
  if (!m) return null;
  const guess = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  const off = zoneOffsetMs(new Date(guess));
  let t = guess - off;
  const off2 = zoneOffsetMs(new Date(t));
  if (off2 !== off) t = guess - off2;
  return new Date(t).toISOString();
}
